'use client'

import { useId } from 'react'
import { useTranslations } from 'next-intl'

import { ArrowRight } from 'lucide-react'

import type { TagShelf } from '@/lib/blog/moods'
import { Button } from '@/components/ui/button'
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from '@/components/ui/carousel'
import { MoodPostCard, toPostCardData } from '@/components/blog/mood-post-card'

import { openMood } from './mood-filter'

/**
 * The blog home's in-season shelf: the committed current season's posts as a
 * scrollable row of cards. Which season is current is config, never a clock
 * reading, so this section renders identically on server and client
 * (`invariants.md#ssr-safe-rendered-dates`) and editorial can run ahead of the
 * calendar.
 *
 * The cards are plain anchors — real post links in the server HTML. Only the
 * "see all" control opens the in-place filter, whose views must stay
 * uncrawlable.
 */
export const BlogSeasonSection = ({ shelf }: { shelf: TagShelf | null }) => {
    const t = useTranslations('blogHome.season')
    const headingId = useId()

    if (!shelf || shelf.posts.length === 0) {
        return null
    }

    return (
        <section
            aria-labelledby={headingId}
            className="mx-auto w-full max-w-[1400px] px-4 py-10 md:px-10 md:py-14"
            data-testid="season-section"
        >
            <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2
                        id={headingId}
                        className="text-2xl font-bold tracking-tight text-text-primary md:text-[28px]"
                    >
                        {t('heading')}
                    </h2>
                    <p className="mt-1 text-[15px] text-text-secondary md:text-base">
                        {t('subheading', { season: shelf.label })}
                    </p>
                </div>
                <Button variant="outline" onClick={() => openMood(shelf.slug)}>
                    {t('seeAll', { season: shelf.label })}
                    <ArrowRight aria-hidden="true" className="size-4" />
                </Button>
            </div>

            <Carousel opts={{ align: 'start' }} data-testid="season-carousel">
                {/* Vertical padding inside the clipping viewport: the track sits in an
                    `overflow-hidden` box, so without room the cards' shadows — which
                    are the only edge they have — get shaved off, most visibly along
                    the bottom. */}
                <CarouselContent className="-ml-4 py-2">
                    {shelf.posts.map((post) => (
                        <CarouselItem
                            key={post.slug}
                            className="basis-4/5 pl-4 sm:basis-1/2 lg:basis-1/3 xl:basis-1/4"
                        >
                            <MoodPostCard post={toPostCardData(post)} />
                        </CarouselItem>
                    ))}
                </CarouselContent>
                <CarouselPrevious className="left-2" />
                <CarouselNext className="right-2" />
            </Carousel>
        </section>
    )
}
